// File: src/components/QRScannerModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert } from '@mui/material';

const QRScannerModal = ({ open, onClose, onDecoded }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const streamRef = useRef(null);
  const rafRef = useRef(0);
  const detectorRef = useRef(null);

  const stop = async () => {
    setRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) {
      stop();
      return;
    }
    setError('');

    const start = async () => {
      try {
        // Prefer back camera on mobile
        const constraints = { video: { facingMode: { ideal: 'environment' } }, audio: false };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        // Try BarcodeDetector if available
        if ('BarcodeDetector' in window) {
          try {
            // eslint-disable-next-line no-undef
            detectorRef.current = new BarcodeDetector({ formats: ['qr_code'] });
          } catch {}
        }

        setRunning(true);

        const loop = async () => {
          if (!running || !videoRef.current) return;
          try {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            if (canvas && video.videoWidth && video.videoHeight) {
              const ctx = canvas.getContext('2d');
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

              if (detectorRef.current && detectorRef.current.detect) {
                const codes = await detectorRef.current.detect(canvas);
                if (codes && codes.length > 0) {
                  const value = codes[0].rawValue || codes[0].value || '';
                  if (value) {
                    onDecoded?.(value);
                    onClose?.();
                    stop();
                    return;
                  }
                }
              }
            }
          } catch (e) {
            // swallow and continue
          }
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      } catch (e) {
        setError('Camera access failed. Please allow camera permissions and try again.');
      }
    };
    start();

    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={() => { onClose?.(); stop(); }} fullWidth maxWidth="sm">
      <DialogTitle>Scan QR</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', bgcolor: 'black', borderRadius: 2, overflow: 'hidden' }}>
          <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', borderRadius: 1, px: 1 }}>
            <Typography variant="caption">Center the QR code in the frame</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { onClose?.(); stop(); }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRScannerModal;


